import { QueryExpr } from '@apis/Resources';
import { DataGrid, gridCurrencyCellRenderer } from '@root/Components/DataGrid';
import { DataGridModel } from '@root/Components/DataGrid/DataGridModel';
import { ColumnConfig, ColumnGroupConfig, ISelectionStrategy, IDataSource, DataGridState, DataSourceConfig } from '@root/Components/DataGrid/Models';
import { useDiMemo } from '@root/Services/DI';
import { EventEmitter, useEventValue } from '@root/Services/EventEmitter';
import { FormatService, NamedFormats } from '@root/Services/FormatService';
import { inject, injectable } from 'tsyringe';
import { UserPreferenceService } from '@root/Services/Customers/UserPreferenceService';
import { AdjustmentRulesResult, InvoiceAdjustmentInfo, InvoiceAdjustmentService } from '@root/Services/Invoices/InvoiceAdjustmentService';
import { InvoiceSchemaService, IBaseInvoiceRecord, IReallocatedInvoiceRecord } from '@root/Services/Invoices/InvoiceSchemaService';
import { InvoiceApiService } from '@root/Services/Invoices/InvoiceApiService';
import { IQueryExpr, Query } from '@apis/Invoices/model';
import { FillerSwitch } from '@root/Design/Filler';
import { exprBuilder, FieldInfo, groupExprs, SchemaService, TypeInfo, ValuesGroupOtherText } from '@root/Services/QueryExpr';
import { EmptyDataText } from '@root/Components/Text/VisibleSpaces';
import { Platform } from '@root/Services/PlatformService';
import { SupportedCurrency, useUserPreferredCurrency } from '@root/Services/Customers/CurrencyPreferenceService';

export type ReconciliationData = ReconciliationGridRow[];

export type ReconciliationGridRow = {
    value?: string;
    parent?: ReconciliationGridRow;
    depth: number;
    [key: string]: any;
};

export interface IReconciliationProvider {
    key: string;

    /** Column group config for header grouping */
    groupConfig: Record<string, ColumnGroupConfig>;

    /** Default dimension to group by */
    defaultDimension: string;

    /** Optional: override default sort for the grid state */
    defaultSort?: DataGridState['sort'];

    /** Display footer for total row */
    displayFooter: boolean;

    /** Footer location */
    footerLocation: 'bottom' | 'top' | 'both';

    /** Get the adjustment rules for this mode */
    getAdjustmentRules(month: Date, adjSvc: InvoiceAdjustmentService): Promise<AdjustmentRulesResult>;

    /** Called after platform is resolved, before columns or data are built. Use to adjust dimension or other config. */
    onPlatformResolved?(platform: Platform): void;

    /** Fetch and assemble the final row data (query, merge, post-process) */
    fetchResults(
        rules: InvoiceAdjustmentInfo[],
        parentFilters: IQueryExpr[],
        month: Date,
        invoiceApi: InvoiceApiService
    ): Promise<ReconciliationGridRow[]>;

    /** Build the base columns (dimension + cost columns) */
    createBaseColumns(fmt: NamedFormats, currency: SupportedCurrency): ColumnConfig<ReconciliationGridRow>[];

    /** Build columns for adjustment rules */
    createRuleColumns(rules: InvoiceAdjustmentInfo[], fmt: NamedFormats, currency: SupportedCurrency): ColumnConfig<ReconciliationGridRow>[];
}

export class ShowbackReconciliationProvider implements IReconciliationProvider {
    public key = 'showback';
    private readonly xb = exprBuilder<IBaseInvoiceRecord & IReallocatedInvoiceRecord>().builder;
    public groupConfig: Record<string, ColumnGroupConfig> = {
        'Tag Rules': { color: '#039855' },
        'Showback Rules': { color: '#DC6803' },
    };
    public defaultDimension: string;
    public defaultSort: DataGridState['sort'] = [{ Direction: 'Desc', Expr: { field: 'invoiceAmount' } }];
    public displayFooter: boolean = false;
    public footerLocation: 'bottom' | 'top' | 'both' = 'top';

    constructor(defaultDimension: string, private granularity: 'daily' | 'monthly' = 'monthly') {
        this.defaultDimension = defaultDimension;
    }

    async getAdjustmentRules(month: Date, adjSvc: InvoiceAdjustmentService): Promise<AdjustmentRulesResult> {
        return adjSvc.getAdjustmentRules(month);
    }

    async fetchResults(
        rules: InvoiceAdjustmentInfo[],
        parentFilters: IQueryExpr[],
        month: Date,
        invoiceApi: InvoiceApiService
    ): Promise<ReconciliationGridRow[]> {
        const { finalAmountsQuery, originalAmountsQuery } = this.createQueries(rules, this.defaultDimension, parentFilters);

        const [finalAmtResults, origAmtResults] = await Promise.all([
            this.executeQuery(finalAmountsQuery, month, invoiceApi),
            !originalAmountsQuery ? null : this.executeQuery(originalAmountsQuery, month, invoiceApi),
        ]);

        const finalAmts = (finalAmtResults?.Results ?? []) as ReconciliationGridRow[];
        const origAmts = (origAmtResults?.Results ?? []) as ReconciliationGridRow[];
        return this.mergeResults(rules, finalAmts, origAmts);
    }

    createBaseColumns(fmt: NamedFormats, currency: SupportedCurrency): ColumnConfig<ReconciliationGridRow>[] {
        return [
            this.dimensionCol(),
            this.costCol('Invoice Amount', 'invoiceAmount', 'Invoice', fmt, currency),
            this.costCol('Showback Amount', 'showbackAmount', 'Showback', fmt, currency),
            this.costCol('Showback (Amortized) Amount', 'showbackAmortizedAmount', 'Showback (Amortized)', fmt, currency),
        ];
    }

    createRuleColumns(rules: InvoiceAdjustmentInfo[], fmt: NamedFormats, currency: SupportedCurrency): ColumnConfig<ReconciliationGridRow>[] {
        return rules.map((rule) => {
            const field = rule.fieldName.toString();
            return {
                accessor: field,
                id: field,
                defaultWidth: 130,
                type: 'number',
                cellRenderer: gridCurrencyCellRenderer((v) => v[field] ?? 0, fmt, currency),
                groupName: rule.type === 'Tag' ? 'Tag Rules' : rule.type === 'Sys' ? 'Billing Adjustments' : 'Showback Rules',
                header: rule.rule.Name ?? field,
                helpText: rule.rule.RuleDescription,
            };
        });
    }

    private createQueries(rules: InvoiceAdjustmentInfo[], dimension: string, parentFilters: IQueryExpr[]) {
        const { xb } = this;

        const finalAmountsQuery: Query = {
            Select: [
                ...xb.select({
                    value: xb.values(xb.fromExpr<string>({ field: dimension }), null, ValuesGroupOtherText),
                    invoiceAmount: xb.sum(xb.model.BilledCost),
                    showbackAmount: xb.sum(xb.model.AdjustedCashCost),
                    showbackAmortizedAmount: xb.sum(xb.model.AdjustedAmortizedCost),
                }),
                ...rules.map((r) => ({
                    Alias: r.fieldName.toString(),
                    Expr: this.createRuleSumExpr(r),
                })),
            ],
            Where: parentFilters.length ? groupExprs('and', parentFilters) : undefined,
        };

        const origDim = !rules.some((r) => r.type === 'Tag' && r.rule.TagKey === dimension)
            ? null
            : dimension.replace(/^(resourceTags)\/([^:]+):(.+)/, '$1/orig:$3');

        const originalAmountsQuery = !origDim
            ? null
            : ({
                  Select: [
                      ...xb.select({
                          value: xb.values(xb.fromExpr<string>({ field: origDim }), null, ValuesGroupOtherText),
                          invoiceAmount: xb.sum(xb.model.BilledCost),
                      }),
                      ...rules
                          .map((r) => (r.type !== 'Tag' ? null : { Alias: r.fieldName.toString(), Expr: this.createTagRuleSumExpr(r) }))
                          .filter((r) => r),
                  ],
                  Where: groupExprs('and', [...parentFilters]),
              } as Query);

        return { finalAmountsQuery, originalAmountsQuery };
    }

    private executeQuery(query: Query, month: Date, invoiceApi: InvoiceApiService) {
        return this.granularity === 'daily' ? invoiceApi.queryByUsageMonth(query, month) : invoiceApi.queryMonthlyRollup(query, [month]);
    }

    private mergeResults(rules: InvoiceAdjustmentInfo[], finalAmts: ReconciliationGridRow[], origAmts: ReconciliationGridRow[]) {
        const tagFields = rules.map((r) => (r.type !== 'Tag' ? null : r.fieldName.toString())!).filter((r) => r);
        const negateTagAmts = (item?: ReconciliationGridRow) => {
            if (item) {
                for (const field of tagFields) {
                    item[field!] = -(item?.[field!] ?? 0);
                }
            }
            return item;
        };
        const origAmtMap = origAmts?.reduce((res, item) => res.set(item.value!, item), new Map<string, ReconciliationGridRow>());
        const finalAmtMap = finalAmts.reduce((res, item) => res.set(item.value!, item), new Map<string, ReconciliationGridRow>());
        const values = new Set<string>([...finalAmtMap.keys(), ...origAmtMap.keys()]);

        return Array.from(values).map(
            (v) =>
                ({
                    ...negateTagAmts(origAmtMap.get(v)),
                    ...finalAmtMap.get(v),
                    value: v === ValuesGroupOtherText ? null : v,
                } as ReconciliationGridRow)
        );
    }

    private dimensionCol(): ColumnConfig<ReconciliationGridRow> {
        return {
            accessor: 'value',
            header: 'Dimension',
            defaultWidth: 200,
            id: 'Dimension',
            type: 'string',
            sortField: 'Dimension',
            defaultFixed: true,
            filter: { filterType: 'string', name: 'Dimension', filterField: 'Dimension' },
            cellRenderer: (r) => (r.value ? <>{r.value}</> : <EmptyDataText />),
        };
    }

    private costCol(
        header: string,
        accessor: string,
        label: string,
        fmt: NamedFormats,
        currency: SupportedCurrency
    ): ColumnConfig<ReconciliationGridRow> {
        return {
            header,
            accessor: accessor as keyof ReconciliationGridRow,
            defaultWidth: 150,
            id: accessor,
            type: 'number',
            align: 'right',
            sortField: accessor,
            defaultFixed: true,
            headerRenderer: () => (
                <div style={{ lineHeight: '25px', height: 50 }}>
                    {label} <br /> Amount
                </div>
            ),
            cellRenderer: gridCurrencyCellRenderer((v) => (v as any)[accessor] ?? 0, fmt, currency),
            filter: {
                filterField: accessor,
                name: header,
                filterType: 'number',
                fieldPickerRenderer: () => header,
            },
        };
    }

    private createRuleSumExpr(rule: InvoiceAdjustmentInfo) {
        return rule.type === 'Tag' ? this.createTagRuleSumExpr(rule) : this.createAllocRuleSumExpr(rule);
    }

    private createTagRuleSumExpr(rule: InvoiceAdjustmentInfo & { type: 'Tag' }) {
        const { xb } = this;
        return xb.resolve(
            xb.aggIf(
                xb.fromExpr<string>({ field: rule.rule.TagKey }).eq(rule.rule.TagValue ?? ''),
                xb.sum(xb.fromExpr<number>({ field: rule.fieldName.toString() }))
            )
        );
    }

    private createAllocRuleSumExpr(rule: InvoiceAdjustmentInfo) {
        const { xb } = this;
        return xb.resolve(xb.sum(xb.fromExpr<number>({ field: rule.fieldName.toString() })));
    }
}

export class BillingReconciliationProvider implements IReconciliationProvider {
    public key = 'billing';
    public groupConfig: Record<string, ColumnGroupConfig> = {
        'Billing Adjustments': { color: '#D92D20' },
    };
    public defaultDimension = 'product/ProductName';
    public displayFooter: boolean = true;
    public footerLocation: 'bottom' | 'top' | 'both' = 'top';

    private static readonly dimensionByPlatform: Record<Platform, string> = {
        Aws: 'product/ProductName',
        Azure: 'MeterCategory',
        Gcp: 'product/ProductName',
    };

    constructor(private companyId: number) {}

    async getAdjustmentRules(month: Date, adjSvc: InvoiceAdjustmentService): Promise<AdjustmentRulesResult> {
        return adjSvc.getSystemAdjustmentRules(month, this.companyId);
    }

    onPlatformResolved(platform: Platform) {
        this.defaultDimension = BillingReconciliationProvider.dimensionByPlatform[platform] ?? this.defaultDimension;
    }

    async fetchResults(
        rules: InvoiceAdjustmentInfo[],
        parentFilters: IQueryExpr[],
        month: Date,
        invoiceApi: InvoiceApiService
    ): Promise<ReconciliationGridRow[]> {
        const query = this.createQuery(rules, this.defaultDimension, parentFilters);
        const response = await invoiceApi.queryBillingReconciliation(query, month, this.companyId);
        const rows = (response?.Results ?? []) as ReconciliationGridRow[];
        return this.normalizeAndSort(rows, rules);
    }

    createBaseColumns(fmt: NamedFormats, currency: SupportedCurrency): ColumnConfig<ReconciliationGridRow>[] {
        return [
            {
                accessor: 'value',
                header: 'Service',
                defaultWidth: 200,
                id: 'Dimension',
                type: 'string',
                sortField: 'Dimension',
                defaultFixed: true,
                filter: { filterType: 'string', name: 'Service', filterField: 'Dimension' },
                cellRenderer: (r) =>
                    r.value === ValuesGroupOtherText ? <EmptyDataText text="Other" /> : r.value ? <>{r.value}</> : <EmptyDataText />,
            },
            {
                header: 'Original Amount',
                accessor: 'originalCost',
                defaultWidth: 150,
                id: 'originalCost',
                type: 'number',
                align: 'right',
                sortField: 'originalCost',
                defaultFixed: true,
                headerRenderer: () => (
                    <div style={{ lineHeight: '25px', height: 50 }}>
                        Original <br /> Amount
                    </div>
                ),
                cellRenderer: gridCurrencyCellRenderer((v) => v.originalCost ?? 0, fmt ?? 'number-with-two-decimals', currency),
                filter: {
                    filterField: 'originalCost',
                    name: 'Original Amount',
                    filterType: 'number',
                    fieldPickerRenderer: () => 'Original Amount',
                },
            },
        ];
    }

    createRuleColumns(rules: InvoiceAdjustmentInfo[], fmt: NamedFormats, currency: SupportedCurrency): ColumnConfig<ReconciliationGridRow>[] {
        const result: ColumnConfig<ReconciliationGridRow>[] = rules.map((rule) => {
            const field = rule.fieldName.toString();
            const header = rule.rule.Name || field.replace('AdjSys', 'Adjustment ').toString();
            return {
                accessor: field,
                id: field,
                defaultWidth: 130,
                type: 'number',
                cellRenderer: gridCurrencyCellRenderer((v) => (v as any)[field] ?? 0, fmt ?? 'number-with-two-decimals', currency),
                groupName: 'Billing Adjustments',
                header: header,
                helpText: rule.rule.RuleDescription,
                filter: {
                    filterField: field,
                    name: header,
                    filterType: 'number',
                    fieldPickerRenderer: () => header,
                },
            };
        });

        result.push({
            header: 'New Amount',
            accessor: 'newAmount',
            defaultWidth: 150,
            id: 'newAmount',
            type: 'number',
            align: 'right',
            sortField: 'newAmount',
            defaultFixed: true,
            headerRenderer: () => (
                <div style={{ lineHeight: '25px', height: 50 }}>
                    New <br /> Amount
                </div>
            ),
            cellRenderer: gridCurrencyCellRenderer((v) => v.newAmount ?? 0, fmt ?? 'number-with-two-decimals', currency),
            filter: {
                filterField: 'newAmount',
                name: 'New Amount',
                filterType: 'number',
                fieldPickerRenderer: () => 'New Amount',
            },
        });

        return result;
    }

    private createQuery(rules: InvoiceAdjustmentInfo[], dimension: string, parentFilters: IQueryExpr[]): Query {
        return {
            Select: [
                {
                    Alias: 'value',
                    Expr: { Operation: 'values', Operands: [{ Field: dimension }, null, { Value: ValuesGroupOtherText }] } as IQueryExpr,
                },
                {
                    Alias: 'originalCost',
                    Expr: { Operation: 'sum', Operands: [{ Field: 'OriginalCost' }] } as IQueryExpr,
                },
                {
                    Alias: 'newAmount',
                    Expr: { Operation: 'sum', Operands: [{ Field: 'BilledCost' }] } as IQueryExpr,
                },
                ...rules.map((r) => ({
                    Alias: r.fieldName.toString(),
                    Expr: { Operation: 'sum', Operands: [{ Field: r.fieldName.toString() }] } as IQueryExpr,
                })),
            ],
            Where: parentFilters.length ? groupExprs('and', parentFilters) : undefined,
            Sort: [{ Direction: 'Desc', Expr: { Field: 'BilledCost' } }],
        };
    }

    private normalizeAndSort(rows: ReconciliationGridRow[], rules: InvoiceAdjustmentInfo[]): ReconciliationGridRow[] {
        return rows
            .map((row) => {
                const normalized = { ...row } as Record<string, any>;
                normalized.originalCost = row.originalCost ?? 0;
                normalized.newAmount = row.newAmount ?? 0;
                for (const r of rules) {
                    const key = r.fieldName.toString();
                    normalized[key] = normalized[key] ?? 0;
                }
                return normalized as ReconciliationGridRow;
            })
            .sort((a, b) => (b.newAmount ?? 0) - (a.newAmount ?? 0));
    }
}

@injectable()
export class ReconciliationGridModel {
    public gridModel?: DataGridModel;
    public columns!: ColumnConfig<ReconciliationGridRow>[];
    public groupConfig: Record<string, ColumnGroupConfig> = {};
    public datasource!: DataSourceConfig<ReconciliationGridRow>;
    public initializing = new EventEmitter(true);
    public month?: Date;
    public currency!: SupportedCurrency;
    public moneyFormat?: NamedFormats;
    public childAccessor = {
        hasChildren: (item: ReconciliationGridRow) => item.depth < (this.gridModel?.gridState?.groupBy?.length ?? 0),
    };
    public defaultState!: DataGridState;
    public schemaSvc!: SchemaService;
    public schemaFilter = (item: FieldInfo | TypeInfo) => ('fields' in item ? item.fields : [item]).some((f) => f.typeName === 'string');
    public platform?: Platform;

    private provider!: IReconciliationProvider;

    public constructor(
        @inject(UserPreferenceService) private readonly userPrefSvc: UserPreferenceService,
        @inject(InvoiceApiService) private readonly invoiceApi: InvoiceApiService,
        @inject(InvoiceSchemaService) public readonly invoiceSchemaSvc: InvoiceSchemaService,
        @inject(InvoiceAdjustmentService) private readonly invoiceAdjustmentSvc: InvoiceAdjustmentService
    ) {}

    public init(month: Date, moneyFormat: NamedFormats, currency: SupportedCurrency, provider: IReconciliationProvider) {
        this.month = month;
        this.moneyFormat = moneyFormat;
        this.currency = currency;
        this.provider = provider;
        this.groupConfig = provider.groupConfig;
        this.initialize();
        return this;
    }

    public async initialize() {
        try {
            this.initializing.emit(true);

            const { rules, platform } = await this.provider.getAdjustmentRules(this.month!, this.invoiceAdjustmentSvc);
            this.platform = platform;
            this.provider.onPlatformResolved?.(platform);
            const activeRules = rules ?? [];
            const fmt = this.moneyFormat ?? 'number-with-two-decimals';
            const baseCols = this.provider.createBaseColumns(fmt, this.currency);
            const ruleCols = this.provider.createRuleColumns(activeRules, fmt, this.currency);
            this.columns = [...baseCols, ...ruleCols];
            this.defaultState = {
                columns: this.columns.map((col) => ({ id: col.id, width: col.defaultWidth, fixed: col.defaultFixed })),
                filters: [],
                sort: this.provider.defaultSort ?? [],
                groupBy: [this.provider.defaultDimension].map((g) => ({ id: g, sortDir: 'Asc', sortMode: 'value' })),
            };

            this.datasource = await this.provider.fetchResults(activeRules, [], this.month!, this.invoiceApi);

            if (this.provider.displayFooter) {
                const rows = this.datasource as ReconciliationGridRow[];
                const totals = this.computeTotals(rows, this.columns);
                this.applyFooterRenderers(this.columns, totals, fmt);
            }

            const types = await this.invoiceSchemaSvc.getMonthlySchema();
            this.schemaSvc = new SchemaService(types);

            for (const column of this.columns) {
                column.noReorder = true;
                column.noRemove = true;
            }
        } finally {
            this.initializing.emit(false);
        }
    }

    private computeTotals(rows: ReconciliationGridRow[], columns: ColumnConfig<ReconciliationGridRow>[]): Record<string, number> {
        const totals: Record<string, number> = {};
        const numericCols = columns.filter((c) => c.type === 'number');
        for (const col of numericCols) {
            const key = col.accessor as string;
            totals[key] = rows.reduce((sum, row) => sum + ((row as any)[key] ?? 0), 0);
        }
        return totals;
    }

    private applyFooterRenderers(columns: ColumnConfig<ReconciliationGridRow>[], totals: Record<string, number>, fmt: NamedFormats) {
        for (const col of columns) {
            if (col.type === 'string') {
                col.footerRenderer = () => 'Total';
            } else if (col.type === 'number') {
                const key = col.accessor as string;
                const renderer = gridCurrencyCellRenderer(() => totals[key] ?? 0, fmt ?? 'number-with-two-decimals', this.currency);
                col.footerRenderer = () => renderer({} as ReconciliationGridRow);
            }
        }
    }

    public attach = (gridModel: DataGridModel) => {
        this.gridModel = gridModel;
    };
}

export function ReconciliationGrid({
    month,
    moneyFormat,
    provider,
    onFilterChanged,
    selectionStrategy,
}: {
    month: Date;
    moneyFormat: NamedFormats;
    provider: IReconciliationProvider;
    onFilterChanged?: (filters: QueryExpr[]) => void;
    selectionStrategy?: ISelectionStrategy<ReconciliationGridRow>;
}) {
    const providerKey = provider.key;
    const currency = useUserPreferredCurrency();
    const gridModel = useDiMemo(ReconciliationGridModel, [month.toISOString(), provider.key, moneyFormat, provider.defaultDimension], (m) =>
        m.init(month, moneyFormat, currency, provider)
    );
    const loading = useEventValue(gridModel.initializing);
    const gridKey = `$ReconciliationGrid-${providerKey}-${month.toISOString()}`;
    const stateKey = `$ReconciliationGrid-${providerKey}`;

    return (
        <FillerSwitch loading={loading}>
            {() => (
                <DataGrid
                    key={gridKey}
                    statePersistence={{ key: stateKey }}
                    state={gridModel.defaultState}
                    columns={gridModel.columns}
                    onModelLoaded={gridModel.attach}
                    dataSource={gridModel.datasource}
                    childAccessor={gridModel.childAccessor}
                    groupByAsRows
                    headerHeight={50}
                    groupConfig={gridModel.groupConfig}
                    showCount={false}
                    showHeaderGroups
                    hideColumnSelector
                    footerPosition={provider.footerLocation}
                    renderFooter={provider.displayFooter}
                />
            )}
        </FillerSwitch>
    );
}
